import * as Yup from "yup";
import moment from "moment";
import { types, BadgeColorSchemeType } from "@vilocnv/allsetra-core";
import { TimeLineItemProps } from "components/ui/LocationTimeline/LocationTimeline";
import {
  IRideBulkUpdateKeys,
  IRideBulkUpdateMode,
  IRideBulkUpdateDriver,
  IRideBulkUpdateDriverOrKey
} from "../types";
import { DateTime } from "luxon";

export enum ObjectRideModeEnum {
  BUSINESS = 1,
  PRIVATE = 2,
  COMMUTE = 3,
  MIXED = 4
}

export const OBJECT_RIDE_MODES = (translator: any): Record<number, string> =>
  ({
    1: translator("objectRideModes.1"),
    2: translator("objectRideModes.2"),
    3: translator("objectRideModes.3"),
    4: translator("objectRideModes.4")
  }) as const;

export const OBJECT_RIDE_MODES_OPTIONS = (translator: any) => {
  const objectRideModes = OBJECT_RIDE_MODES(translator);

  return Object.keys(objectRideModes)
    .filter((item: any) => item != 4)
    .map((item: any) => ({
      value: item,
      label: objectRideModes[item]
    }));
};

export const OBJECT_RIDE_MODES_COLOR: Record<number, BadgeColorSchemeType> = {
  1: "purple",
  2: "info",
  3: "success",
  4: "warning"
} as const;

export const transformObjectRideDataForForm = (
  objectRide: types.IObjectRide & {
    driver?: any;
    driverId?: string;
    key?: any;
    privateMileage?: number;
    changeReason?: string;
  }
): any => {
  return {
    uniqueId: objectRide.uniqueId,
    keyId: objectRide.key?.uniqueId || "",
    driverId: objectRide?.driver?.uniqueId || objectRide?.driverId || "",
    rideMode: objectRide.rideMode,
    privateMileage: objectRide.privateMileage ?? undefined,
    changeReason: objectRide.changeReason || "",
    comments: objectRide.comments || "",
    startAddress: objectRide.startAddress || "",
    endAddress: objectRide.endAddress || "",
    startAddressPointOfInterest: undefined,
    endAddressPointOfInterest: undefined,
    correctedEndAddress: objectRide.correctedEndAddress || "",
    correctedEndLatitude: objectRide.correctedEndLatitude || 0,
    correctedEndLongitude: objectRide.correctedEndLongitude || 0,
    correctedStartAddress: objectRide.correctedStartAddress || "",
    correctedStartLatitude: objectRide.correctedStartLatitude || 0,
    correctedStartLongitude: objectRide.correctedStartLongitude || 0
  };
};

export const updateObjectRideFormValidationSchema = (t: any) =>
  Yup.object({
    keyId: Yup.string().label("Driver"),
    rideMode: Yup.string()
      .label("Ride Mode")
      .required(t("validation.rideModeRequired")),
    privateMileage: Yup.number()
      .label("Private Mileage")
      .min(0, t("validation.cannotBeNegative")),
    changeReason: Yup.string()
      .label("Change Reason")
      .trim()
      .min(1, t("validation.fieldRequired"))
      .required(t("validation.fieldRequired"))
  });

export const transformObjectRideLocationsForTimeline = (
  objectRideLocations: Array<types.IObjectRideLocation>,
  objectRide: any
): Array<TimeLineItemProps> => {
  return objectRideLocations.length > 1
    ? objectRideLocations.map((item) => ({
        resolvedAddress: item.address,
        date: item?.date,
        latitude: item?.latitude - 0.00001,
        longitude: item?.longitude,
        startAddress:
          objectRide?.startAddress || objectRide?.correctedStartAddress,
        endAddress: objectRide?.endAddress || objectRide?.correctedEndAddress
      }))
    : [
        {
          resolvedAddress: objectRide?.startAddress,
          date: objectRide?.startDate,
          latitude: objectRide?.startLatitude - 0.00001,
          longitude: objectRide?.startLongitude,
          startAddress:
            objectRide?.startAddress || objectRide?.correctedStartAddress,
          endAddress: objectRide?.endAddress || objectRide?.correctedEndAddress
        },
        {
          resolvedAddress: objectRide?.endAddress,
          date: objectRide?.endDate,
          latitude: objectRide?.endLatitude - 0.00001,
          longitude: objectRide?.endLongitude,
          startAddress:
            objectRide?.startAddress || objectRide?.correctedStartAddress,
          endAddress: objectRide?.endAddress || objectRide?.correctedEndAddress
        }
      ];
};

export const getObjectRideTotalTimeInMinutes = (
  objectRide: types.IObjectRide
) => {
  return moment(objectRide.endDate).diff(objectRide.startDate, "minutes");
};

export const getRidesBulkUpdateKeyInitialValues = (
  rides: string[]
): IRideBulkUpdateKeys => ({
  rides,
  keyId: null,
  changeReason: ""
});

export const getRidesBulkUpdateModeInitialValues = (
  rides: string[]
): IRideBulkUpdateMode => ({
  rides,
  rideMode: null,
  changeReason: ""
});

export const ridesBulkUpdateKeyValidationSchema = (t: any) =>
  Yup.object({
    keyId: Yup.string()
      .label("Driver")
      .required(
        t("keyId.required", {
          ns: "formFieldsTranslation"
        })
      ),
    changeReason: Yup.string()
      .label("Change Reason")
      .trim()
      .min(
        1,
        t("changeReason.required", {
          ns: "formFieldsTranslation"
        })
      )
      .required(
        t("changeReason.required", {
          ns: "formFieldsTranslation"
        })
      )
  });

export const ridesBulkUpdateModeValidationSchema = (t: any) =>
  Yup.object({
    rideMode: Yup.number()
      .label("Mode")
      .required(
        t("rideType.required", {
          ns: "formFieldsTranslation"
        })
      ),
    changeReason: Yup.string()
      .label("Change Reason")
      .trim()
      .min(
        1,
        t("changeReason.required", {
          ns: "formFieldsTranslation"
        })
      )
      .required(
        t("changeReason.required", {
          ns: "formFieldsTranslation"
        })
      )
  });

export const getRidesBulkUpdateDriverInitialValues = (
  rides: string[]
): IRideBulkUpdateDriver => ({
  rides,
  driverId: null,
  changeReason: ""
});

export const ridesBulkUpdateDriverValidationSchema: Yup.Schema = Yup.object({
  driverId: Yup.string().label("Driver").required()
});

export const getRidesBulkUpdateDriverOrKeyInitialValues = (
  rides: string[]
): IRideBulkUpdateDriverOrKey => ({
  rides,
  driverId: null,
  keyId: null,
  changeReason: ""
});

export const ridesBulkUpdateDriverOrKeyValidationSchema = (t: any) =>
  Yup.object({
    driverId: Yup.string().nullable(),
    keyId: Yup.string().nullable(),
    changeReason: Yup.string()
      .label("Change Reason")
      .trim()
      .min(
        1,
        t("changeReason.required", {
          ns: "formFieldsTranslation"
        })
      )
      .required(
        t("changeReason.required", {
          ns: "formFieldsTranslation"
        })
      )
  }).test(
    "one-of-driver-or-key",
    "Either driver or key is required",
    (values: any) => {
      const hasDriver = !!values?.driverId;
      const hasKey = !!values?.keyId;
      return (hasDriver && !hasKey) || (!hasDriver && hasKey);
    }
  );

export const getObjectRideKeyValueData = (locations: any[]) => {
  const data: any = {};
  locations?.forEach(
    (item) =>
      (data[`${DateTime.fromISO(item?.date).toFormat("dd MMM yyyy, HH:mm")} `] =
        `${item?.speed?.toFixed(2)} km/h`)
  );
  return data;
};
